import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaFileAlt, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import materialTransferService from "../../../../../services/web/material-transfer-service.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import CellRendererAttachButton from "../../../../../components/ag-grid/cell-renderer-attach-button.jsx";

const initialFilter = {
    material: "",
}

const MaterialTransferList = () => {
    const navigate = navigateHook();
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);

    const columnDefs = useMemo(() => [
        {field: "id", hide: true},
        {field: "date", headerName: "Data/hora", width: 130, comparator: aggridUtils.agGridCompareDateTime},
        {field: "storage", headerName: "Depósito de origem", comparator: aggridUtils.lowerUpCaseComparator},
        {field: "destination", headerName: "Depósito de destino", comparator: aggridUtils.lowerUpCaseComparator},
        {field: "group", headerName: "Grupo", comparator: aggridUtils.lowerUpCaseComparator},
        {field: "material", headerName: "Material", flex: 1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator},
        {field: "requester", headerName: "Almoxarife solicitante", comparator: aggridUtils.lowerUpCaseComparator},
        {field: "quantity", headerName: "Quantidade", width: 120},
        {field: "transfer_attach", headerName: "Anexo", width: 100,
            cellRenderer: ({ data }) => {
                return <CellRendererAttachButton attachId={data.transfer_attach} modalTitle="Anexo" />
            }
        }
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const {data: transfers = [], isLoading} = useQuery({
        queryKey: ['materials-transfers'],
        queryFn: async () => {
            const {data} = await materialTransferService.list();
            return data;
        },
    });

    const onChange = useCallback(({target: {id, value}}) => {
        setFilter(prev => ({...prev, [id]: value}));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const openForm = () => {
        const urlDev = `/materials/transfers/form`;
        navigate(urlDev)
    }

    const openReport = () => {
        const url = `/reports/material_movements?operation=transfer`;
        navigate(url, true)
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Transferências entre depósitos
                </Template.Title>
                <div className={"flex gap-2"}>
                    <Button onClick={openReport}>
                        <FaFileAlt/><span className="font-bold">Relatório</span>
                    </Button>
                    <Button onClick={openForm}>
                        <FaPlus/><span className="font-bold">Adicionar transferência</span>
                    </Button>
                </div>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className="col-span-12 flex gap-4">
                                <InputForm id="material" name="material" value={filter.material} onChange={onChange}
                                           label="Material" classNameWrapper={"w-full"}/>
                                <div className="flex justify-start gap-2">
                                    <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)]"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={transfers}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme({noHover: true})}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
};

export default MaterialTransferList;